import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Repeat, X, Image as ImageIcon } from 'lucide-react';

const AdminRecurringForm = ({
  recurringData,
  setRecurringData,
  locations,
  onSubmit,
  onOpenImageLibrary,
  onClose
}) => {
  const toggleSpecificDay = (day) => {
    setRecurringData(prev => ({
      ...prev,
      specific_days: prev.specific_days.includes(day)
        ? prev.specific_days.filter(d => d !== day)
        : [...prev.specific_days, day]
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background-paper border border-white/10 rounded-sm p-8 mb-12"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl font-bold">
          CREATE <span className="text-primary">RECURRING CLASSES</span>
        </h2>
        <Button variant="ghost" onClick={onClose} data-testid="close-recurring-btn">
          <X className="w-5 h-5" />
        </Button>
      </div>

      <form onSubmit={onSubmit}>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <Label htmlFor="rec-title">Class Title</Label>
            <Input
              id="rec-title"
              value={recurringData.title}
              onChange={(e) => setRecurringData({...recurringData, title: e.target.value})}
              className="bg-background border-white/10"
              required
              data-testid="recurring-title-input"
            />
          </div>
          <div>
            <Label htmlFor="rec-instructor">Instructor</Label>
            <Input
              id="rec-instructor"
              value={recurringData.instructor}
              onChange={(e) => setRecurringData({...recurringData, instructor: e.target.value})}
              className="bg-background border-white/10"
              required
              data-testid="recurring-instructor-input"
            />
          </div>
        </div>

        <div className="mb-6">
          <Label htmlFor="rec-description">Description</Label>
          <Textarea
            id="rec-description"
            value={recurringData.description}
            onChange={(e) => setRecurringData({...recurringData, description: e.target.value})}
            className="bg-background border-white/10"
            rows={3}
            required
            data-testid="recurring-description-input"
          />
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <div>
            <Label htmlFor="rec-location">Location</Label>
            <select
              id="rec-location"
              value={recurringData.location}
              onChange={(e) => setRecurringData({...recurringData, location: e.target.value})}
              className="w-full bg-background border border-white/10 rounded-sm p-2 text-white h-10"
              data-testid="recurring-location-select"
            >
              {locations.map(loc => (
                <option key={loc.location_id} value={loc.name}>{loc.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="rec-start">Start Date & Time</Label>
            <Input
              id="rec-start"
              type="datetime-local"
              value={recurringData.start_datetime}
              onChange={(e) => setRecurringData({...recurringData, start_datetime: e.target.value})}
              className="bg-background border-white/10"
              required
              data-testid="recurring-start-input"
            />
          </div>
          <div>
            <Label htmlFor="rec-duration">Duration (mins)</Label>
            <Input
              id="rec-duration"
              type="number"
              value={recurringData.duration}
              onChange={(e) => setRecurringData({...recurringData, duration: parseInt(e.target.value)})}
              className="bg-background border-white/10"
              min="15"
              required
              data-testid="recurring-duration-input"
            />
          </div>
          <div>
            <Label htmlFor="rec-price">Price (£)</Label>
            <Input
              id="rec-price"
              type="number"
              step="0.01"
              value={recurringData.price}
              onChange={(e) => setRecurringData({...recurringData, price: parseFloat(e.target.value)})}
              className="bg-background border-white/10"
              min="0"
              required
              data-testid="recurring-price-input"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div>
            <Label htmlFor="rec-capacity">Capacity</Label>
            <Input
              id="rec-capacity"
              type="number"
              value={recurringData.capacity}
              onChange={(e) => setRecurringData({...recurringData, capacity: parseInt(e.target.value)})}
              className="bg-background border-white/10"
              min="1"
              required
              data-testid="recurring-capacity-input"
            />
          </div>
          <div>
            <Label htmlFor="rec-recurrence">Recurrence Pattern</Label>
            <select
              id="rec-recurrence"
              value={recurringData.recurrence_type}
              onChange={(e) => setRecurringData({...recurringData, recurrence_type: e.target.value})}
              className="w-full bg-background border border-white/10 rounded-sm p-2 text-white h-10"
              data-testid="recurring-pattern-select"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
              <option value="specific_days">Specific Days</option>
            </select>
          </div>
          <div>
            <Label htmlFor="rec-weeks">Weeks in Advance</Label>
            <Input
              id="rec-weeks"
              type="number"
              value={recurringData.weeks_in_advance}
              onChange={(e) => setRecurringData({...recurringData, weeks_in_advance: parseInt(e.target.value)})}
              className="bg-background border-white/10"
              min="1"
              max="12"
              required
              data-testid="recurring-weeks-input"
            />
          </div>
        </div>

        {recurringData.recurrence_type === 'specific_days' && (
          <div className="mb-6">
            <Label className="mb-2 block">Select Days</Label>
            <div className="flex flex-wrap gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                <Button
                  key={day}
                  type="button"
                  variant={recurringData.specific_days.includes(index) ? 'default' : 'outline'}
                  className={recurringData.specific_days.includes(index) ? 'bg-primary' : 'border-white/20'}
                  onClick={() => toggleSpecificDay(index)}
                  data-testid={`day-btn-${day.toLowerCase()}`}
                >
                  {day}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="mb-6">
          <Label htmlFor="rec-image">Class Image</Label>
          <div className="flex gap-2">
            <Input
              id="rec-image"
              value={recurringData.image_url}
              onChange={(e) => setRecurringData({...recurringData, image_url: e.target.value})}
              className="bg-background border-white/10 flex-1"
              placeholder="https://... or use library/upload"
              data-testid="recurring-image-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenImageLibrary('recurring')}
              className="border-white/20"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Library
            </Button>
          </div>
          {recurringData.image_url && (
            <img
              src={recurringData.image_url}
              alt="Preview"
              className="w-24 h-24 object-cover rounded mt-2 border border-white/10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </div>

        <Button type="submit" className="bg-primary hover:bg-primary-hover font-bold w-full" data-testid="create-recurring-btn">
          <Repeat className="w-4 h-4 mr-2" />
          CREATE RECURRING CLASSES
        </Button>
      </form>
    </motion.div>
  );
};

export default AdminRecurringForm;
